import {
  BuyerFormValues,
  CarrierFormValues, ConsigneeFormValues, ConsignorFormValues, SellerFormValues, TraderType,
} from 'features/declaration/models/schemas/tradersSchema'
import { AdditionalInformationType, AddressFormValues } from 'features/declaration/models/schemas/commonConsignmentSchemas'
import { CreateOrUpdateTransitOperationRequestDeclarationTypeEnum } from 'api/models'
import { hasText } from 'shared/lib/common-util'

export function isConsignee(trader: TraderType): trader is ConsigneeFormValues {
  return trader.target === 'CONSIGNEE'
}
export function isConsignor(trader: TraderType): trader is ConsignorFormValues {
  return trader.target === 'CONSIGNOR'
}

export function isCarrier(trader: TraderType): trader is CarrierFormValues {
  return trader.target === 'CARRIER'
}

export function isSeller(trader: TraderType): trader is SellerFormValues {
  return trader.target === 'SELLER'
}

export function isBuyer(trader: TraderType): trader is BuyerFormValues {
  return trader.target === 'BUYER'
}

export function isTraderWithPersonType(
  trader: TraderType,
): trader is ConsignorFormValues | ConsigneeFormValues | SellerFormValues | BuyerFormValues {
  return 'typeOfPerson' in trader
}

export function isTraderWithContactPerson(trader: TraderType): trader is ConsignorFormValues | ConsigneeFormValues | CarrierFormValues {
  return 'contactPerson' in trader
}

export function nonNullAddress(address: AddressFormValues) {
  return hasText(address.city)
    || hasText(address.country)
    || hasText(address.addressLine)
    || hasText(address.postcode)
}

function nonNullTrader(trader: ConsigneeFormValues | ConsignorFormValues | SellerFormValues | BuyerFormValues) {
  if (isConsignor(trader)) {
    if (hasText(trader.contactPerson?.name) || hasText(trader.contactPerson?.email) || hasText(trader.contactPerson?.telephone)) {
      return true
    }
  }
  if (hasText(trader.identificationNumber)) {
    return true
  }

  if (hasText(trader.name)) return true

  return trader.address && nonNullAddress(trader.address)
}

export function nonNullConsignee(consignee: ConsigneeFormValues) {
  return consignee.target === 'CONSIGNEE' && nonNullTrader(consignee)
}
export function nonNullConsignor(consignor: ConsignorFormValues) {
  return consignor.target === 'CONSIGNOR' && nonNullTrader(consignor)
}

export function nonNullSeller(seller: SellerFormValues) {
  return seller.target === 'SELLER' && nonNullTrader(seller)
}

export function nonNullBuyer(buyer: BuyerFormValues) {
  return buyer.target === 'BUYER' && nonNullTrader(buyer)
}

export function nonNullCarrier(carrier: CarrierFormValues) {
  return carrier.target === 'CARRIER' && nonNullTrader(carrier)
}

export function nonNullConsignorIgnoringContactPerson(consignor: ConsignorFormValues) {
  if (!isConsignor(consignor)) return false
  if (hasText(consignor.identificationNumber)) {
    return true
  }

  if (hasText(consignor.name)) return true

  return consignor.address && nonNullAddress(consignor.address)
}

export function someAdditionalInformationItemHasExsCode(additionalInformations: AdditionalInformationType[]) {
  return additionalInformations.some((item) => item.code === '30600')
}

export function tradersEqual(
  previous: ConsignorFormValues | ConsigneeFormValues | null,
  current: ConsignorFormValues | ConsigneeFormValues | null,
) {
  if (previous === null && current === null) return true

  if (current === null) return true
  if (previous === null) return true
  if (previous.target !== current.target) {
    throw Error('Traders not comparable')
  }

  if (previous.name !== current.name) return false
  if (previous.identificationNumber !== current.identificationNumber) return false

  if (previous.address?.city !== current.address?.city) return false
  if (previous.address?.country !== current.address?.country) return false
  if (previous.address?.postcode !== current.address?.postcode) return false
  if (previous.address?.addressLine !== current.address?.addressLine) return false
  if (previous.address?.streetNumber !== current.address?.streetNumber) return false

  if (isConsignor(previous) && isConsignor(current)) {
    if (previous.contactPerson?.name !== current.contactPerson?.name) return false
    if (previous.contactPerson?.email !== current.contactPerson?.email) return false
    if (previous.contactPerson?.telephone !== current.contactPerson?.telephone) return false
  }

  return true
}

export const isEnsType = (
  declarationType: CreateOrUpdateTransitOperationRequestDeclarationTypeEnum,
) => CreateOrUpdateTransitOperationRequestDeclarationTypeEnum.ENS === declarationType
